import type {
  ComponentVisibility,
  FormComponent,
  FormDSLContext,
  FormStep,
  ValidationRules,
} from '@commenda/types';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import type { PreviewFillHandler } from '../hooks/FormContext';
import type { PreviewTestingPanelProps } from './PreviewTestingPanel';
import { usePreviewTestRunner } from './usePreviewTestRunner';

interface UseLinkedFormPreviewTestingArgs {
  isPreview: boolean;
  steps: FormStep[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  formData: Record<string, unknown>;
  dslContext?: FormDSLContext;
  updateField: (fieldName: string, value: unknown) => void;
  validateField: (
    fieldName: string,
    value: unknown,
    rules?: ValidationRules
  ) => string | null;
  isFieldVisible: (visibility?: ComponentVisibility) => boolean;
  validateSteps: (steps: FormStep[]) => {
    isValid: boolean;
    firstErrorField?: string;
  };
  componentMap: Record<string, FormComponent>;
  errors: Record<string, string>;
  setErrors: Dispatch<SetStateAction<Record<string, string>>>;
  setExpandedModules: Dispatch<SetStateAction<Set<string>>>;
  isCommendaOSPrefilling: boolean;
}

interface LinkedFormPreviewTesting {
  registerPreviewFillHandler: (
    fieldId: string,
    handler: PreviewFillHandler | null
  ) => void;
  /** Null outside preview, so the caller renders no panel. */
  panelProps: PreviewTestingPanelProps | null;
}

const isFieldNeverDisabled = () => false;

/**
 * Preview testing for a linked form's own local state. Keeps a fill-handler
 * map separate from the host form's, so self-filling components inside the
 * linked form register here rather than with the host's runner. Only fills
 * and validates; never touches the linked form's submit.
 */
export function useLinkedFormPreviewTesting({
  isPreview,
  steps,
  currentStep,
  setCurrentStep,
  formData,
  dslContext,
  updateField,
  validateField,
  isFieldVisible,
  validateSteps,
  componentMap,
  errors,
  setErrors,
  setExpandedModules,
  isCommendaOSPrefilling,
}: UseLinkedFormPreviewTestingArgs): LinkedFormPreviewTesting {
  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => a.order - b.order),
    [steps]
  );

  const fillHandlersRef = useRef<Map<string, PreviewFillHandler>>(new Map());
  const registerPreviewFillHandler = useCallback(
    (id: string, handler: PreviewFillHandler | null) => {
      if (handler) fillHandlersRef.current.set(id, handler);
      else fillHandlersRef.current.delete(id);
    },
    []
  );
  const getPreviewFillHandler = useCallback(
    (id: string) => fillHandlersRef.current.get(id),
    []
  );

  const validateCurrentStep = useCallback(() => {
    const step = sortedSteps[currentStep];
    return step ? validateSteps([step]) : { isValid: true };
  }, [sortedSteps, currentStep, validateSteps]);

  const clearFieldError = useCallback(
    (fieldName: string) => {
      setErrors((prev) => {
        if (!(fieldName in prev)) return prev;
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    },
    [setErrors]
  );

  const getFieldError = useCallback(
    (fieldName: string) => errors[fieldName],
    [errors]
  );

  const expandCurrentStepModules = useCallback(() => {
    const stepModules = sortedSteps[currentStep]?.modules ?? [];
    setExpandedModules(new Set(stepModules.map((module) => module.id)));
  }, [sortedSteps, currentStep, setExpandedModules]);

  const runner = usePreviewTestRunner({
    isPreview,
    visibleSteps: sortedSteps,
    currentStep,
    setCurrentStep,
    formData,
    dslContext,
    updateField,
    validateField,
    isFieldVisible,
    isFieldDisabled: isFieldNeverDisabled,
    validateCurrentStep,
    componentMap,
    getPreviewFillHandler,
    isCommendaOSPrefilling,
    clearFieldError,
    getFieldError,
    expandCurrentStepModules,
  });

  const panelProps: PreviewTestingPanelProps | null = isPreview
    ? {
        onFillStep: runner.fillCurrentStep,
        onRunToEnd: runner.runToEnd,
        onStop: runner.stopRun,
        runStatus: runner.runStatus,
        lastFillResult: runner.lastFillResult,
        runReport: runner.runReport,
        activityLabel: runner.activityLabel,
      }
    : null;

  return { registerPreviewFillHandler, panelProps };
}
